import {inject, injectable} from 'inversify';

import type {ChannelApiModel} from '../../../../api/channel';
import type {PostApiModel} from '../../../../api/channel/post';
import type {ConferenceApiModel} from '../../../../api/conference';
import type {CourseApiModel} from '../../../../api/course';
import type {EventApiModel} from '../../../../api/event';
import type {EditionApiModel, ProgramApiModel} from '../../../../api/program';
import type {UserApiModel} from '../../../../api/user';
import type {Nullable} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';
import type {WsMessageModel} from '../WsMessage.model';

import {WsMessageFactory} from '../WsMessage.factory';
import {WsMessageExpandJrpc} from './WsMessage.expand.jrpc';

@injectable()
export class WsMessageMapperJrpc
  implements
    IApiDataMapper<
      WsMessageMapperJrpc.ApiData,
      WsMessageModel.Props,
      WsMessageModel
    >
{
  constructor(
    @inject(WsMessageFactory)
    private readonly factory: WsMessageFactory,
    @inject(WsMessageExpandJrpc)
    public readonly expand: WsMessageExpandJrpc,
  ) {}

  mapDataToModel(apiData: WsMessageMapperJrpc.ApiData): WsMessageModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: WsMessageMapperJrpc.ApiData): WsMessageModel.Props {
    const id = apiData.event._id;
    const type = this.type(apiData);
    const courseId = this.courseId(apiData);
    const channelId = this.channelId(apiData);
    const postId = this.postId(apiData);
    const actor = this.actor(apiData);
    const file = this.file(apiData);
    const conference = this.conference(apiData);

    return {
      actor,
      apiData,
      channelId,
      conference,
      courseId,
      file,
      id,
      postId,
      type,
    };
  }

  private actor(
    props: WsMessageMapperJrpc.ApiData,
  ): WsMessageModel.Props['actor'] {
    if (!props.actor) {
      return null;
    }

    const {_id, name} = props.actor;

    return {
      id: _id,
      name,
    };
  }

  private channelId(
    props: WsMessageMapperJrpc.ApiData,
  ): WsMessageModel.Props['channelId'] {
    return props.channel?._id ?? '';
  }

  private conference(
    props: WsMessageMapperJrpc.ApiData,
  ): WsMessageModel.Props['conference'] {
    if (!props.conference) {
      return null;
    }

    const {_id, type} = props.conference;

    const result: WsMessageModel.Conference = {
      id: _id,
      type: type === 'chat' ? 'chat' : 'live',
    };

    return result;
  }

  private courseId(
    props: WsMessageMapperJrpc.ApiData,
  ): WsMessageModel.Props['courseId'] {
    return props.course?._id ?? '';
  }

  private file(
    props: WsMessageMapperJrpc.ApiData,
  ): WsMessageModel.Props['file'] {
    if (!props.file) {
      return null;
    }

    const id = props.file._id;
    const userId = props.file.user_id;

    return {id, userId};
  }

  private postId(
    props: WsMessageMapperJrpc.ApiData,
  ): WsMessageModel.Props['postId'] {
    return props.post?._id ?? '';
  }

  private type(props: WsMessageMapperJrpc.ApiData) {
    return props.event.type as WsMessageModel.Props['type'];
  }
}

export namespace WsMessageMapperJrpc {
  export type ApiData = {
    _id: string;
    actor?: Pick<
      UserApiModel,
      '_id' | 'coach_schedule_until' | 'gender' | 'name' | 'timezone'
    > & {
      google_account_email: Nullable<string>;
    };
    channel?: Pick<ChannelApiModel, '_id' | 'title' | 'type'>;
    conference?: Pick<ConferenceApiModel, '_id' | 'type'>;
    course?: Pick<
      CourseApiModel,
      | '_id'
      | 'coach_ids'
      | 'edition_id'
      | 'end_date'
      | 'label'
      | 'program_id'
      | 'start_date'
      | 'stop_reason'
    >;
    edition?: Pick<EditionApiModel, '_id' | 'group_size' | 'title'> & {
      currency: Nullable<'EUR' | 'GBP' | 'USD'>;
      price: Nullable<number>;
    };
    event: Pick<EventApiModel, '_id' | 'created' | 'type'>;
    file?: {
      _id: string;
      categories: ['audio'];
      repr: string;
      type: 'file';
      user_id: string;
    };
    post?: Pick<PostApiModel, '_id' | 'body' | 'created'>;

    program?: Pick<ProgramApiModel, '_id' | 'coach_ids' | 'title'>;
  };

  export type ExpandedApiData = ApiData & WsMessageExpandJrpc.ExpandedFields;
}
